import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import catchError from '../../components/global/CatchError'
import { SERVER_URL } from '../../config'
import { useLangSelected } from '../../hooks/useLangSelected'
import apiClient from '../../services/apiClient'
import { CourtType, Exercise, GameType, Intensity } from '../../types/exercise.type'
import { Skill } from '../../types/skill.type'

// Types pour les différents formats de compétences possibles
type SkillWithId = { id: number }
type SkillWithSkillId = { skillId: number }
type SkillWithNestedSkill = { skill: { id: number } }
type AnySkill = SkillWithId | SkillWithSkillId | SkillWithNestedSkill

import ExerciseForm from './ExerciseForm'

// Type pour l'exercice en cours d'édition
type EditExerciseType = Omit<Exercise, 'skills'> & {
    skillIds: number[]
    skills: Array<{ skill: Skill }>
    title: string
    description: string
    peopleCount: number
    intensity: Intensity
    gameType: GameType
    courtType: CourtType
}

type CoachExerciseFormModalProps = {
    show: boolean
    onClose: () => void
    exercise?: EditExerciseType
    onSuccess: () => void
    skills: Skill[]
}

export default function CoachExerciseFormModal({
    show,
    onClose,
    exercise,
    onSuccess,
    skills,
}: Readonly<CoachExerciseFormModalProps>) {
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const navigate = useNavigate()
    const lng = useLangSelected()

    const { mutate: createExercise } = useMutation({
        mutationFn: async (data: Omit<Exercise, 'id'>) => {
            await apiClient.post('/exercises', data)
            toast.success(t('coach.exercises.createSuccess'))
        },
        onSuccess: onSuccess,
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' })
            } else {
                const errorMessage = error.response?.data?.message ?? t('coach.exercises.createError')
                toast.error(errorMessage)
            }
        },
        onSettled: () => setIsSubmitting(false),
    })

    const { mutate: updateExercise } = useMutation({
        mutationFn: async (data: Exercise) => {
            await apiClient.patch(`/exercises/${data.id}`, data)
            toast.success(t('coach.exercises.updateSuccess'))
        },
        onSuccess: onSuccess,
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' })
            } else {
                const errorMessage = error.response?.data?.message ?? t('coach.exercises.updateError')
                toast.error(errorMessage)
            }
        },
        onSettled: () => setIsSubmitting(false),
    })

    const handleSubmit = (data: any) => {
        setIsSubmitting(true)
        if (exercise?.id) {
            updateExercise({
                ...data,
                id: exercise.id,
            })
        } else {
            createExercise(data)
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {exercise?.id ? t('common.edit') : t('common.create')} {t('coach.exercises.exercise')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ExerciseForm
                    initial={
                        exercise
                            ? {
                                  title: exercise.title,
                                  description: exercise.description,
                                  peopleCount: exercise.peopleCount,
                                  intensity: exercise.intensity,
                                  gameType: exercise.gameType,
                                  courtType: exercise.courtType,
                                  skillIds: exercise.skillIds || [],
                              }
                            : undefined
                    }
                    skills={skills}
                    loading={isSubmitting}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                />
            </Modal.Body>
        </Modal>
    )
}
